import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Button from 'dpl/common/design_system/Button';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import LocationAutocompleteContainer from 'dpl/search/containers/LocationAutocompleteContainer';
import useSearchFiltering from 'dpl/search_result_filters/hooks/useSearchFiltering';
import { isLessThanBreakpoint } from 'dpl/util/grid';

import MenuDrawer from '../SearchResultsHeader/MenuDrawer';
import LocationResult from './LocationResult';
import SuggestedLocationButton from './SuggestedLocationButton';

const IS_LT_MD = isLessThanBreakpoint('md');

function LocationSelectionModalContent({
  onLocationSelect,
  onLocationClear,
  searchValue,
  onInputChange,
  suggestedLocationName,
  onSuggestionClick
}) {
  const { resultsType } = useSearchFiltering();

  return (
    <div className="LocationSelectionModalContent">
      <h5 className="fw-normal font-sans font-16 secondary lh-title mb8 mb4-md">
        We’ll show {resultsType} near you and strong matches further away.
      </h5>
      <div>
        <label
          htmlFor="user_location"
          className="db font-12 lh-title secondary mb2"
        >
          Location
        </label>
        <LocationAutocompleteContainer
          classes={{
            menu: 'UserLocation__autocomplete overflow-y-auto mt2-md',
            menuContent: 'br3 overflow-hidden mt4 mt0-md pv2-md ph2-md',
            selectedMenuItem: 'bg-gray-200 br3'
          }}
          menuCloseTimeout={IS_LT_MD ? 0 : 300}
          onSelect={onLocationSelect}
          onClear={IS_LT_MD ? onLocationClear : null}
          query={searchValue}
          inputProps={{
            name: 'user_location',
            className: 'input-reset font-16 ba b--stone-700 br3 w-100 pv4 ph4',
            onChange: onInputChange,
            placeholder: 'Location'
          }}
          clearIconProps={{ className: 'tertiary' }}
          ResultsItemComponent={LocationResult}
          omitStates
          stripCountryCode
        />
      </div>
      {suggestedLocationName && (
        <div className="mt6">
          <p className="db font-12 lh-title secondary mb2">Suggestion</p>
          <SuggestedLocationButton
            locationName={suggestedLocationName}
            onClick={onSuggestionClick}
          />
        </div>
      )}
      <p className="font-12 lh-title secondary mt2 pr20-md mr2-md">
        You may see {resultsType} outside your area, since it’s typical to
        travel for the right ethical breeder.
      </p>
    </div>
  );
}

LocationSelectionModalContent.propTypes = {
  onLocationSelect: PropTypes.func.isRequired,
  onLocationClear: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  onInputChange: PropTypes.func.isRequired,
  suggestedLocationName: PropTypes.string,
  onSuggestionClick: PropTypes.func.isRequired
};

LocationSelectionModalContent.defaultProps = {
  suggestedLocationName: null
};

export default function LocationSelectionModal({
  locationName,
  onSelect,
  onClose
}) {
  const [searchValue, setSearchValue] = useState(locationName);

  const suggestedLocationName = useSelector(({ server }) => {
    const { ipLocationName } = server;
    return locationName !== ipLocationName ? ipLocationName : null;
  });

  function handleInputChange(e) {
    setSearchValue(e.target.value);
  }

  function handleClearButtonClick() {
    setSearchValue('');
    onSelect(null);
    onClose();
  }

  function handleLocationSelect(location) {
    onSelect(location);
    onClose();
  }

  function handleLocationClear() {
    setSearchValue('');
  }

  function handleSuggestionClick() {
    onSelect({ name: suggestedLocationName });
    onClose();
  }

  useEffect(() => {
    setSearchValue(locationName);
  }, [locationName]);

  if (IS_LT_MD) {
    return (
      <MenuDrawer
        title="Your location"
        position="bottom"
        onClose={onClose}
        classes={{
          modal: 'LocationSelectionDrawer',
          content: 'flex-auto flex flex-column justify-between ph5',
          wrapper: 'flex-auto flex flex-column'
        }}
      >
        <LocationSelectionModalContent
          onLocationSelect={handleLocationSelect}
          onLocationClear={handleLocationClear}
          searchValue={searchValue}
          onInputChange={handleInputChange}
          suggestedLocationName={suggestedLocationName}
          onSuggestionClick={handleSuggestionClick}
        />
        <div className="flex justify-center bg-white pt5">
          <Button
            variant="outline"
            className="pv4 ph12"
            disabled={!searchValue}
            onClick={handleClearButtonClick}
          >
            Clear
          </Button>
        </div>
      </MenuDrawer>
    );
  }

  return (
    <MiniOverlayModal
      className="LocationSelectionModal"
      closeButtonClassName="ba b--light-gray bg-white br-100 padding-h-6 padding-v-6 mt5 mt4-md mr4"
      maxWidth="600px"
      onCloseClick={onClose}
      scrollableContent={IS_LT_MD}
    >
      <div className="sticky top-0 bg-white pt4 pt14-md pb3 pb2-md ph5 ph16-md br4 br--top bb bb-0-ns b--light-gray">
        <h4 className="font-24 md:font-32">Your location</h4>
      </div>
      <div className="pt6 pt0-md pb14 pb4-md ph5 ph16-md">
        <LocationSelectionModalContent
          onLocationSelect={handleLocationSelect}
          onLocationClear={handleLocationClear}
          searchValue={searchValue}
          onInputChange={handleInputChange}
          suggestedLocationName={suggestedLocationName}
          onSuggestionClick={handleSuggestionClick}
        />
      </div>
      <div className="flex justify-center justify-start-ns fixed static-ns left-0 right-0 bottom-0 bg-white br--bottom br4-ns pt6 pb6 pb14-md ph5 ph16-md">
        <Button
          variant="outline"
          className="pv4 ph12"
          disabled={!searchValue}
          onClick={handleClearButtonClick}
        >
          Clear
        </Button>
      </div>
    </MiniOverlayModal>
  );
}

LocationSelectionModal.propTypes = {
  locationName: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

LocationSelectionModal.defaultProps = {
  locationName: null
};
